import React, { useMemo, useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Layout, Menu, Avatar, Dropdown, Switch, Tooltip, Modal, Input, message } from 'antd';
import { 
  DashboardOutlined, 
  CloudOutlined, 
  UserOutlined,
  LogoutOutlined,
  BulbOutlined,
  CrownOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  DisconnectOutlined,
  LockOutlined,
  FileTextOutlined,
  TeamOutlined,
  ApartmentOutlined
  ,CalculatorOutlined
  ,AuditOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import ClientHome from './ClientHome';
import FinOpsDashboard from './FinOpsDashboard';
import ConnectClouds from './ConnectClouds';
import AWSSetup from './AWSSetup';
import AWSDashboard from './AWSDashboard';
import GCPDashboard from './GCPDashboard';
import ConnectedAccounts from './ConnectedAccounts';
import AdminUsers from './AdminUsers';
import AdminTenants from './AdminTenants';
import AdminRecommendations from './AdminRecommendations';
import CommercialAssessments from './CommercialAssessments';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useFeatures } from '../context/FeatureContext';
import { useCloud } from '../context/CloudContext';
import PremiumFeatures from './PremiumFeatures';
import { appVersion, buildCommit, releaseNotes } from '../data/releaseNotes';
import './Dashboard.css';

const { Header, Sider, Content } = Layout;

const Dashboard: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [changelogModalOpen, setChangelogModalOpen] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const { user, logout, changePassword } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { isPremium } = useFeatures();
  const { isConnected } = useCloud();
  const navigate = useNavigate();
  const location = useLocation();
  const azureConnected = isConnected('azure');
  const awsConnected = isConnected('aws');
  const gcpConnected = isConnected('gcp');
  const isAdmin = user?.roles?.some(role => ['admin', 'platform-admin'].includes(role.toLowerCase()));
  const isPlatformAdmin = user?.roles?.some(role => role.toLowerCase() === 'platform-admin');
  const hasCommercialAccess = isAdmin || user?.roles?.some(role => role.toLowerCase() === 'commercial');
  const isCommercialOnly = !isAdmin && user?.roles?.some(role => role.toLowerCase() === 'commercial');

  // Atualizar horário a cada segundo
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Auto refresh dos dados
  useEffect(() => {
    if (!autoRefresh) return;
    
    const refreshTimer = setInterval(() => {
      // Disparar evento customizado para componentes filhos atualizarem
      window.dispatchEvent(new CustomEvent('autoRefresh'));
    }, 60000); // 1 minuto
    
    return () => clearInterval(refreshTimer);
  }, [autoRefresh]);

  const menuItems = useMemo(() => {
    const items: any[] = [];

    if (!isCommercialOnly) {
      items.push({
        key: '/',
        icon: <DashboardOutlined />,
        label: 'Início',
      });
    }

    if (!isCommercialOnly && azureConnected) {
      items.push({
        key: '/finops',
        icon: <DashboardOutlined />,
        label: 'Dashboard Azure',
      });
    }

    if (!isCommercialOnly && awsConnected) {
      items.push({
        key: '/aws-dashboard',
        icon: <CloudOutlined />,
        label: 'Dashboard AWS',
      });
    }

    if (!isCommercialOnly && gcpConnected) {
      items.push({
        key: '/gcp-dashboard',
        icon: <CloudOutlined />,
        label: 'Dashboard GCP',
      });
    }

    if (!isCommercialOnly) items.push(
      // Conectar/gerenciar contas é uma acao de credencial, restrita a quem
      // administra a integracao (mesmo criterio do backend,
      // canManageCloudAccounts em routes/cloudAccounts.ts). Um usuario
      // cliente (finops/viewer) so ve e usa 'Contas e Relatorios' - a lista
      // e os relatorios em si, sem o fluxo de conectar uma conta nova.
      ...(hasCommercialAccess ? [{
        key: '/connect',
        icon: <CloudOutlined />,
        label: 'Conectar Clouds',
      }] : []),
      {
        key: '/reports',
        icon: <FileTextOutlined />,
        label: 'Contas e Relatórios',
      },
      ...(isAdmin ? [{
        key: '/admin/users',
        icon: <TeamOutlined />,
        label: 'Usuários',
      }] : []),
      ...(isPlatformAdmin ? [{
        key: '/admin/tenants',
        icon: <ApartmentOutlined />,
        label: 'Tenants',
      }] : []),
      {
        key: '/premium',
        icon: <CrownOutlined style={{ color: isPremium ? '#faad14' : undefined }} />,
        label: isPremium ? 'Premium ✓' : 'Premium',
      }
    );

    if (hasCommercialAccess) {
      items.push({
        type: 'group',
        label: 'COMERCIAL',
        children: [
          {
            key: '/commercial/assessments',
            icon: <CalculatorOutlined />,
            label: 'Calculadora Multicloud'
          },
          {
            key: '/admin/recommendations',
            icon: <AuditOutlined />,
            label: 'Recomendações'
          }
        ]
      });
    }

    return items;
  }, [awsConnected, azureConnected, gcpConnected, hasCommercialAccess, isAdmin, isPlatformAdmin, isCommercialOnly, isPremium]);

  const contentBackground = location.pathname === '/' || location.pathname === '/finops' || location.pathname === '/aws-dashboard' || location.pathname === '/gcp-dashboard'
    ? 'transparent'
    : (isDark ? '#111827' : '#fff');
  const latestReleaseDate = releaseNotes[0]?.date
    ? new Date(`${releaseNotes[0].date}T00:00:00`).toLocaleDateString('pt-BR')
    : null;

  const getAwsDashboardPath = () => {
    let cachedData: any = null;
    try {
      cachedData = JSON.parse(localStorage.getItem('awsData') || 'null');
    } catch (error) {
      console.error('Erro ao recuperar contexto AWS salvo:', error);
    }
    const cloudAccountId = cachedData?.cloudAccountId || localStorage.getItem('awsCloudAccountId') || '';
    const reportId = cachedData?.reportId || '';
    if (!cloudAccountId && !reportId) return '/aws-dashboard';

    const params = new URLSearchParams();
    if (cloudAccountId) params.set('cloudAccountId', cloudAccountId);
    if (reportId) params.set('reportId', reportId);
    return `/aws-dashboard?${params.toString()}`;
  };

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key === '/aws-dashboard' ? getAwsDashboardPath() : key);
  };

  const userMenuItems = [
    {
      key: 'change-password',
      icon: <LockOutlined />,
      label: 'Alterar senha',
      onClick: () => setPasswordModalOpen(true),
    },
    {
      key: 'theme',
      icon: <BulbOutlined />,
      label: isDark ? 'Tema Claro' : 'Tema Escuro',
      onClick: toggleTheme,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Sair',
      onClick: logout,
    },
  ];

  const resetPasswordForm = () => {
    setPasswordForm({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
  };

  const handlePasswordChange = async () => {
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      message.error('Preencha todos os campos para alterar a senha.');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      message.error('A confirmação não confere com a nova senha.');
      return;
    }

    if (passwordForm.newPassword.length < 12) {
      message.error('A nova senha deve ter no mínimo 12 caracteres.');
      return;
    }

    setPasswordSaving(true);
    const result = await changePassword(passwordForm.currentPassword, passwordForm.newPassword);
    setPasswordSaving(false);

    if (!result.success) {
      message.error(result.message || 'Erro ao alterar senha.');
      return;
    }

    message.success('Senha alterada com sucesso.');
    setPasswordModalOpen(false);
    resetPasswordForm();
  };

  return (
    <Layout className={`dashboard-shell ${isDark ? 'dashboard-shell--dark' : 'dashboard-shell--light'}`}>
      <Sider 
        className="dashboard-shell__sider"
        collapsible 
        collapsed={collapsed} 
        onCollapse={setCollapsed}
        theme={isDark ? 'dark' : 'light'}
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
        }}
      >
        <div className={`dashboard-brand ${collapsed ? 'dashboard-brand--collapsed' : ''}`}>
          <div className={`dashboard-brand__mark ${collapsed ? 'dashboard-brand__mark--compact' : ''}`}>
            <img src="/assets/incloud-logo.svg" alt="inCloud Tecnologia" />
          </div>
        </div>
        
        <Menu
          theme={isDark ? 'dark' : 'light'}
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={handleMenuClick}
        />
      </Sider>
      
      <Layout className="dashboard-shell__main" style={{ marginLeft: collapsed ? 80 : 200 }}>
        <Header className="dashboard-shell__header">
          <div className="dashboard-header__identity">
            <span className="dashboard-header__eyebrow">Ambiente executivo</span>
            <strong>Olá, {user?.name}</strong>
            <span>
              {currentTime.toLocaleDateString('pt-BR', {
                weekday: 'long',
                day: 'numeric',
                month: 'long'
              })} • {currentTime.toLocaleTimeString('pt-BR')}
            </span>
          </div>

          <div className="dashboard-header__actions">
            <div className="dashboard-cloud-status" aria-label="Status das conexões cloud">
              <Tooltip title={azureConnected ? 'Azure conectado' : 'Azure aguardando conexão'}>
                <button
                  type="button"
                  className={`dashboard-cloud-status__item ${azureConnected ? 'is-connected' : ''}`}
                  onClick={() => navigate(azureConnected ? '/finops' : '/connect')}
                >
                  {azureConnected ? <CheckCircleOutlined /> : <DisconnectOutlined />}
                  <span>Azure</span>
                </button>
              </Tooltip>

              <Tooltip title={awsConnected ? 'AWS conectado' : 'AWS aguardando conexão'}>
                <button
                  type="button"
                  className={`dashboard-cloud-status__item ${awsConnected ? 'is-connected' : ''}`}
                  onClick={() => navigate(awsConnected ? getAwsDashboardPath() : '/connect')}
                >
                  {awsConnected ? <CheckCircleOutlined /> : <DisconnectOutlined />}
                  <span>AWS</span>
                </button>
              </Tooltip>

              <Tooltip title={gcpConnected ? 'GCP conectado' : 'GCP aguardando conexão'}>
                <button
                  type="button"
                  className={`dashboard-cloud-status__item ${gcpConnected ? 'is-connected' : ''}`}
                  onClick={() => navigate(gcpConnected ? '/gcp-dashboard' : '/connect')}
                >
                  {gcpConnected ? <CheckCircleOutlined /> : <DisconnectOutlined />}
                  <span>GCP</span>
                </button>
              </Tooltip>
            </div>

            <Tooltip title="Atualização automática a cada 1 minuto">
              <div className={`dashboard-refresh ${autoRefresh ? 'is-active' : ''}`}>
                <ReloadOutlined />
                <Switch
                  size="small"
                  checked={autoRefresh}
                  onChange={setAutoRefresh}
                />
                <span>Auto</span>
              </div>
            </Tooltip>
            
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <button type="button" className="dashboard-user-button" aria-label="Menu do usuário">
                <Avatar icon={<UserOutlined />} />
              </button>
            </Dropdown>
          </div>
        </Header>
        
        <Content style={{ 
          margin: 24, 
          padding: 24, 
          background: contentBackground,
          borderRadius: 8,
          overflow: 'auto'
        }} className="dashboard-shell__content">
          <Routes>
            <Route path="/" element={isCommercialOnly ? <Navigate to="/commercial/assessments" replace /> : <ClientHome />} />
            <Route path="/finops" element={!isCommercialOnly && azureConnected ? <FinOpsDashboard /> : <Navigate to={isCommercialOnly ? '/commercial/assessments' : '/connect'} replace />} />
            <Route path="/connect" element={!isCommercialOnly && hasCommercialAccess ? <ConnectClouds /> : <Navigate to={isCommercialOnly ? '/commercial/assessments' : '/'} replace />} />
            <Route path="/connect-clouds" element={<Navigate to={isCommercialOnly ? '/commercial/assessments' : '/connect'} replace />} />
            <Route path="/aws-setup" element={!isCommercialOnly && hasCommercialAccess ? <AWSSetup /> : <Navigate to={isCommercialOnly ? '/commercial/assessments' : '/'} replace />} />
            <Route path="/aws-dashboard" element={!isCommercialOnly && awsConnected ? <AWSDashboard /> : <Navigate to={isCommercialOnly ? '/commercial/assessments' : '/connect'} replace />} />
            <Route path="/gcp-dashboard" element={!isCommercialOnly && gcpConnected ? <GCPDashboard /> : <Navigate to={isCommercialOnly ? '/commercial/assessments' : '/connect'} replace />} />
            <Route path="/analytics" element={<Navigate to={isCommercialOnly ? '/commercial/assessments' : (azureConnected ? '/' : '/connect')} replace />} />
            <Route path="/reports" element={!isCommercialOnly ? <ConnectedAccounts /> : <Navigate to="/commercial/assessments" replace />} />
            <Route path="/admin/users" element={isAdmin ? <AdminUsers /> : <Navigate to="/" replace />} />
            <Route path="/admin/tenants" element={isPlatformAdmin ? <AdminTenants /> : <Navigate to="/" replace />} />
            <Route path="/commercial/assessments" element={hasCommercialAccess ? <CommercialAssessments /> : <Navigate to="/" replace />} />
            <Route path="/admin/recommendations" element={hasCommercialAccess ? <AdminRecommendations /> : <Navigate to="/" replace />} />
            <Route path="/premium" element={!isCommercialOnly ? <PremiumFeatures /> : <Navigate to="/commercial/assessments" replace />} />
            <Route path="*" element={<Navigate to={isCommercialOnly ? '/commercial/assessments' : '/connect'} replace />} />
          </Routes>
        </Content>

        {isAdmin && (
          <footer className="dashboard-release-footer">
            <span>xKnow FinOps Cloud v{appVersion}</span>
            {latestReleaseDate && <span>Atualizado em {latestReleaseDate}</span>}
            <span>Build {buildCommit.slice(0, 7)}</span>
            <button type="button" onClick={() => setChangelogModalOpen(true)}>
              Ver changelog
            </button>
          </footer>
        )}
      </Layout>

      <Modal
        title="Alterar senha"
        open={passwordModalOpen}
        okText="Salvar nova senha"
        cancelText="Cancelar"
        confirmLoading={passwordSaving}
        onOk={handlePasswordChange}
        onCancel={() => {
          setPasswordModalOpen(false);
          resetPasswordForm();
        }}
      >
        <div className="dashboard-password-form">
          <label>
            <span>Senha atual</span>
            <Input.Password
              value={passwordForm.currentPassword}
              onChange={(event) => setPasswordForm(prev => ({
                ...prev,
                currentPassword: event.target.value
              }))}
              autoComplete="current-password"
            />
          </label>

          <label>
            <span>Nova senha</span>
            <Input.Password
              value={passwordForm.newPassword}
              onChange={(event) => setPasswordForm(prev => ({
                ...prev,
                newPassword: event.target.value
              }))}
              autoComplete="new-password"
            />
          </label>

          <label>
            <span>Confirmar nova senha</span>
            <Input.Password
              value={passwordForm.confirmPassword}
              onChange={(event) => setPasswordForm(prev => ({
                ...prev,
                confirmPassword: event.target.value
              }))}
              autoComplete="new-password"
            />
          </label>

          <p>
            A senha deve seguir a política do Cognito: mínimo de 12 caracteres,
            com letra maiúscula, letra minúscula e número.
          </p>
        </div>
      </Modal>

      <Modal
        title="Changelog da plataforma"
        open={changelogModalOpen}
        footer={null}
        onCancel={() => setChangelogModalOpen(false)}
      >
        <div className="dashboard-changelog">
          {releaseNotes.map((note) => (
            <article key={`${note.date}-${note.commit || note.title}`} className="dashboard-changelog__item">
              <div>
                <span>{note.category}</span>
                <strong>{note.title}</strong>
              </div>
              <p>{note.description}</p>
              <small>
                v{note.version} • {new Date(`${note.date}T00:00:00`).toLocaleDateString('pt-BR')}
                {note.repository ? ` • ${note.repository}` : ''}
                {note.commit ? ` • commit ${note.commit}` : ''}
              </small>
            </article>
          ))}
        </div>
      </Modal>
    </Layout>
  );
};

export default Dashboard;
